import type { FolioEntry } from './api/types';

export interface ReceiptLine {
  id: string;
  description: string;
  department: string;
  at: string;
  amount: number; // signed: discounts are negative
}

export interface ReceiptTax {
  code: string;
  name: string;
  rateBps: number;
  amount: number;
}

export interface ReceiptData {
  lines: ReceiptLine[];
  payments: { id: string; description: string; at: string; amount: number }[];
  taxes: ReceiptTax[];
  net: number; // before tax
  taxTotal: number;
  gross: number; // what the guest was charged, tax included
  paid: number; // payments received less refunds
  balance: number; // positive = still owed
}

/**
 * Turns a folio's append-only entries into the figures printed on a receipt or invoice.
 * A charge that was voided disappears (and so does its void), exactly as it disappears from the balance.
 * The numbers must agree with the server's folio balance; the tests check that.
 */
export function buildReceipt(entries: FolioEntry[]): ReceiptData {
  const voided = new Set(entries.filter((e) => e.type === 'VOID' && e.voidsEntryId).map((e) => e.voidsEntryId!));
  const live = entries.filter((e) => e.type !== 'VOID' && !voided.has(e.id));

  const lines: ReceiptLine[] = [];
  const payments: ReceiptData['payments'] = [];
  const taxMap = new Map<string, ReceiptTax>();
  let gross = 0;
  let paid = 0;

  for (const e of live) {
    if (e.type === 'CHARGE' || e.type === 'ADJUSTMENT') {
      const sign = e.direction === 'DEBIT' ? 1 : -1;
      gross += sign * e.amount;
      lines.push({ id: e.id, description: e.description, department: e.department, at: e.createdAt, amount: sign * e.amount });
      for (const t of e.taxes) {
        const cur = taxMap.get(t.code) ?? { code: t.code, name: t.name, rateBps: t.rateBps, amount: 0 };
        cur.amount += sign * t.amount;
        taxMap.set(t.code, cur);
      }
    } else if (e.type === 'PAYMENT') {
      paid += e.amount;
      payments.push({ id: e.id, description: e.description, at: e.createdAt, amount: e.amount });
    } else if (e.type === 'REFUND') {
      paid -= e.amount;
      payments.push({ id: e.id, description: e.description, at: e.createdAt, amount: -e.amount });
    }
  }

  const taxes = [...taxMap.values()].filter((t) => t.amount !== 0);
  const taxTotal = taxes.reduce((s, t) => s + t.amount, 0);
  return { lines, payments, taxes, net: gross - taxTotal, taxTotal, gross, paid, balance: gross - paid };
}
